import { AlertCircle, Package } from 'lucide-react';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

interface SelectableItemGridProps<T> {
  items: T[];
  fieldName: string;
  getId: (item: T) => string;
  renderItem: (item: T, isSelected: boolean) => React.ReactNode;
  loading?: boolean;
  emptyMessage?: string;
  isMutiSelect?: boolean;
}

export default function SelectableItem<T>({
  items,
  fieldName,
  getId,
  renderItem,
  loading = false,
  emptyMessage = 'No items available',
  isMutiSelect = true,
}: SelectableItemGridProps<T>) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const value = watch(fieldName);
  const selectedIds: string[] = isMutiSelect
    ? value || []
    : value
      ? [value]
      : [];
  const error = errors[fieldName];

  const toggleSelection = useCallback(
    (itemId: string, checked: boolean) => {
      if (isMutiSelect) {
        const updatedIds = checked
          ? [...selectedIds, itemId]
          : selectedIds.filter((id) => id !== itemId);
        setValue(fieldName, updatedIds, { shouldValidate: true });
      } else {
        setValue(fieldName, checked ? itemId : null, { shouldValidate: true });
      }
    },
    [selectedIds, setValue, fieldName, isMutiSelect],
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded-xl"></div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Package className="mx-auto h-12 w-12 mb-2" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items
        .sort((a, b) => {
          const aSelected = selectedIds.includes(getId(a));
          const bSelected = selectedIds.includes(getId(b));
          // If a is selected but b isn't, put a first
          if (aSelected && !bSelected) return -1;
          if (!aSelected && bSelected) return 1;
          return 0; // keep order if both same
        })
        .map((item) => {
          const itemId = getId(item);
          const isSelected = selectedIds.includes(itemId);
          return (
            <label
              key={itemId}
              className={`
              block p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 
              hover:shadow-md hover:scale-[1.02]
              ${isSelected
                  ? 'bg-primary border-primary text-white shadow-lg'
                  : 'bg-white border-gray-200 hover:border-primary/30'
                }
              ${error ? 'border-red-300' : ''}
            `}
            >
              <input
                type="checkbox"
                value={itemId}
                checked={isSelected}
                className="sr-only"
                onChange={(e) => toggleSelection(itemId, e.target.checked)}
              />
              <div className="flex items-center justify-between">
                {renderItem(item, isSelected)}
                <div
                  className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${isSelected ? 'border-white bg-white' : 'border-gray-300'}
              `}
                >
                  {isSelected && (
                    <div className="min-w-2.5 min-h-2.5 w-2.5 h-2.5 rounded-full bg-primary"></div>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      {error && (
        <div className="flex items-center gap-2 text-red-500 text-sm mt-2">
          <AlertCircle className="h-4 w-4" />
          <span>{error.message as string}</span>
        </div>
      )}
    </div>
  );
}
